import React, { useEffect, useState } from 'react';
import { BACKEND_URL } from '../config';
import fetchWithAuth from '../utils/fetchWithAuth';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ReportsPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [filters, setFilters] = useState({
    vehiclePlate: '',
    driverLicense: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportError, setReportError] = useState('');

  useEffect(() => {
    // Fetch data for report filters
    fetchWithAuth(`${BACKEND_URL}/vehicles`).then(res => res.json()).then(data => setVehicles(data));
    fetchWithAuth(`${BACKEND_URL}/drivers`).then(res => res.json()).then(data => setDrivers(data));
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerateReport = async () => {
    setLoadingReport(true);
    setReportError('');
    setReportData(null);

    const { year, month, vehiclePlate, driverLicense } = filters;
    const date = `${year}-${String(month).padStart(2, '0')}`;

    const payload = {
      kind: "trip_summary",
      params: {
        startDate: date,
        endDate: date, // For a single month report
        ...(vehiclePlate && { vehiclePlate }),
        ...(driverLicense && { driverLicense }),
      }
    };

    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || "Error al generar el reporte.");
      }

      const data = await res.json();
      if (data.summary.totalTrips === 0) {
        setReportError("No se encontraron viajes con los filtros seleccionados.");
      } else {
        setReportData(data);
      }
    } catch (err) {
      setReportError(err.message);
    } finally {
      setLoadingReport(false);
    }
  };

  const handleDownloadPdf = () => {
    if (!reportData) return;

    const doc = new jsPDF();
    const { summary, trips } = reportData;
    const { year, month, vehiclePlate, driverLicense } = filters;

    // Title
    doc.setFontSize(18);
    doc.text('Reporte de Viajes', 14, 22);

    // Filters Info
    doc.setFontSize(11);
    doc.setTextColor(100);
    const monthName = new Date(0, month - 1).toLocaleString('es-ES', { month: 'long' });
    const period = `${monthName.charAt(0).toUpperCase() + monthName.slice(1)} ${year}`;

    const selectedVehicle = vehicles.find(v => v.plate === vehiclePlate);
    const vehicleText = selectedVehicle ? `${selectedVehicle.brand} ${selectedVehicle.model} (${vehiclePlate})` : 'Todos';

    const selectedDriver = drivers.find(d => d.license === driverLicense);
    const driverText = selectedDriver ? selectedDriver.name : 'Todos';

    let startY = 32;
    doc.text(`Periodo: ${period}`, 14, startY);
    doc.text(`Vehículo: ${vehicleText}`, 100, startY);
    startY += 6;
    doc.text(`Conductor: ${driverText}`, 14, startY);
    startY += 10;

    autoTable(doc, {
      startY,
      head: [['Resumen', '']],
      body: [
        ['Viajes Totales', summary.totalTrips],
        ['KM Totales Recorridos', summary.totalKm],
        ['Galones Totales Consumidos', summary.totalGasGallons],
        ['Costo Total (S/)', summary.totalCost.toFixed(2)],
      ],
      theme: 'grid',
      headStyles: { fillColor: [22, 160, 133], fontSize: 12 },
      columnStyles: { 0: { fontStyle: 'bold' } }
    });

    // Trips Table
    const tableHead = [['Fecha', 'Combustible', 'N° galones', 'Precio (S/)', 'Origen', 'Actividad', 'Destino', 'KM Salida', 'KM Llegada']];
    const tableBody = trips.map(trip => [
      trip.date,
      trip.gasGallons > 0 ? 'Sí' : 'No',
      trip.gasGallons || 0,
      trip.cost || 0,
      trip.origin,
      trip.purpose,
      trip.destination,
      trip.startKm,
      trip.endKm
    ]);

    autoTable(doc, {
      head: tableHead,
      body: tableBody,
      startY: doc.lastAutoTable.finalY + 10,
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save(`reporte_viajes_${year}_${String(month).padStart(2, '0')}.pdf`);
  };

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Reportes de Viajes</h1>

      {/* Filters Section */}
      <div className="p-6 bg-white rounded-xl shadow-sm">
        <h2 className="text-xl font-bold mb-4">Filtros del Reporte</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4 items-end">
          {/* Vehicle Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vehículo (Opcional)</label>
            <select name="vehiclePlate" value={filters.vehiclePlate} onChange={handleFilterChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              <option value="">Todos</option>
              {vehicles.map(v => <option key={v.plate} value={v.plate}>{v.brand} {v.model} ({v.plate})</option>)}
            </select>
          </div>
          {/* Driver Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Conductor (Opcional)</label>
            <select name="driverLicense" value={filters.driverLicense} onChange={handleFilterChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              <option value="">Todos</option>
              {drivers.map(d => <option key={d.license} value={d.license}>{d.name}</option>)}
            </select>
          </div>
          {/* Year Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Año</label>
            <select name="year" value={filters.year} onChange={handleFilterChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              {[...Array(5)].map((_, i) => {
                const year = new Date().getFullYear() - i;
                return <option key={year} value={year}>{year}</option>;
              })}
            </select>
          </div>
          {/* Month Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mes</label>
            <select name="month" value={filters.month} onChange={handleFilterChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(0, i).toLocaleString('es-ES', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          {/* Generate Button */}
          <button onClick={handleGenerateReport} disabled={loadingReport} className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300">
            {loadingReport ? "Generando..." : "Mostrar Reporte"}
          </button>
        </div>

        {/* Report Display Area */}
        {loadingReport && <div className="text-center p-4">Cargando reporte...</div>}
        {reportError && <div className="text-center p-4 text-red-600">{reportError}</div>}
        {reportData && (
          <div className="mt-6 border-t pt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Resultados del Reporte</h3>
              <button onClick={handleDownloadPdf} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">
                Descargar PDF
              </button>
            </div>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 text-center">
              <div className="p-4 bg-gray-100 rounded-lg">
                <div className="text-sm text-gray-600">Viajes Totales</div>
                <div className="text-2xl font-bold">{reportData.summary.totalTrips}</div>
              </div>
              <div className="p-4 bg-gray-100 rounded-lg">
                <div className="text-sm text-gray-600">KM Totales</div>
                <div className="text-2xl font-bold">{reportData.summary.totalKm}</div>
              </div>
              <div className="p-4 bg-gray-100 rounded-lg">
                <div className="text-sm text-gray-600">Galones Totales</div>
                <div className="text-2xl font-bold">{reportData.summary.totalGasGallons}</div>
              </div>
              <div className="p-4 bg-gray-100 rounded-lg">
                <div className="text-sm text-gray-600">Costo Total</div>
                <div className="text-2xl font-bold">S/ {reportData.summary.totalCost.toFixed(2)}</div>
              </div>
            </div>
            {/* Trips Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Fecha</th>
                    <th className="px-4 py-2 text-left">Combustible</th>
                    <th className="px-4 py-2 text-left">N° galones</th>
                    <th className="px-4 py-2 text-left">Precio</th>
                    <th className="px-4 py-2 text-left">Lugar Salida</th>
                    <th className="px-4 py-2 text-left">Actividad</th>
                    <th className="px-4 py-2 text-left">Lugar Llegada</th>
                    <th className="px-4 py-2 text-left">KM Salida</th>
                    <th className="px-4 py-2 text-left">KM Llegada</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.trips.map(trip => (
                    <tr key={trip.id} className="border-b">
                      <td className="px-4 py-2">{trip.date}</td>
                      <td className="px-4 py-2">{trip.gasGallons > 0 ? 'Sí' : 'No'}</td>
                      <td className="px-4 py-2">{trip.gasGallons}</td>
                      <td className="px-4 py-2">{trip.cost}</td>
                      <td className="px-4 py-2">{trip.origin}</td>
                      <td className="px-4 py-2">{trip.purpose}</td>
                      <td className="px-4 py-2">{trip.destination}</td>
                      <td className="px-4 py-2">{trip.startKm}</td>
                      <td className="px-4 py-2">{trip.endKm}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
